import React, { useCallback, useEffect, useState } from 'react';
import {
    CCardList,
    DateRange,
    DBlock,
    DCheckbox,
    DDateRangePicker,
    DIdAutocomplete,
    selectPaginated,
    useCardListSort,
    usePaginator,
} from '../../../../dum';
import { Button } from '@mui/material';
import {
    ColumnOrder,
    ExpensesPageQuery,
    ExpensesSortableFields,
    useExpensesPageLazyQuery,
} from '../../../../../services';
import {
    COLUMN_LABELS,
    DATE_FORMAT,
    shortLabel,
    useBoolean,
    useDebounce,
    useNumber,
    useString,
} from '../../../../../helpers';
import dayjs from 'dayjs';
import {
    SDeleteExpenseDialog,
    SGenerateRecurringExpensesDialog,
    SUpdateExpenseDetailsDialog,
    SUpsertExpenseDialog,
} from '../../../../smart';
import { ExpenseCard } from './blocks/expense-card';

type ExpenseDoc = ExpensesPageQuery['paginatedExpenses']['docs'][number];

export function ExpensesPage() {
    const [query, { data, previousData, loading }] =
        useExpensesPageLazyQuery();
    const [upsertOpen, setUpsertOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [generateOpen, setGenerateOpen] = useState(false);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [expenseId, setExpensesId] = useState<null | number>(null);

    // Typed sort: the chip list and the query share the generated
    // ExpensesSortableFields enum — no column-index mapping.
    const { sort, toggle: toggleSort } =
        useCardListSort<ExpensesSortableFields>(ExpensesPage.name);

    const [filter, setFilter] = useString({ KEY: ExpensesPage.name + '_Filter' });
    const debouncedFilter = useDebounce(filter, 400);
    const [startDate, setStartDate] = useString({ KEY: ExpensesPage.name + '_StartDate' });
    const [endDate, setEndDate] = useString({ KEY: ExpensesPage.name + '_EndDate' });
    const [accountId, setAccountId] = useNumber({ KEY: ExpensesPage.name + '_AccountId' });
    const [resourceId, setResourceId] = useNumber({ KEY: ExpensesPage.name + '_ResourceId' });
    const [noReceipt, setNoReceipt] = useBoolean({ KEY: ExpensesPage.name + '_NoReceipt', defaultValue: false });
    const [isTransferComplete, setIsTransferComplete] = useBoolean({ KEY: ExpensesPage.name + '_isTransferComplete', defaultValue: false });
    const [noSupplement, setNoSupplement] = useBoolean({ KEY: ExpensesPage.name + '_NoSupplement', defaultValue: false });

    const { take, setSkip, skip } = usePaginator(
        { KEY: ExpensesPage.name + '_Paginator' },
        [startDate, endDate, debouncedFilter],
    );

    useEffect(() => {
        void query({
            variables: {
                skip,
                take,
                startDate: startDate || null,
                endDate: endDate || null,
                filter: debouncedFilter,
                sortOrder: sort
                    ? sort.order === 'asc'
                        ? ColumnOrder.Asc
                        : ColumnOrder.Desc
                    : null,
                accountId,
                sortField: sort?.field ?? null,
                resourceId,
                noReceipt,
                noSupplement,
                isTransferComplete,
            },
        });
    }, [query, skip, take, startDate, endDate, debouncedFilter, sort, accountId, resourceId, noReceipt, noSupplement, isTransferComplete]);

    const { docs: expenses, count } = selectPaginated(
        data?.paginatedExpenses,
        previousData?.paginatedExpenses,
    );

    const handleEditExpenses = useCallback((expense: ExpenseDoc) => {
        setExpensesId(expense.id);
        setUpsertOpen(true);
    }, []);

    const handleDeleteExpenses = useCallback((expense: ExpenseDoc) => {
        setExpensesId(expense.id);
        setDeleteOpen(true);
    }, []);

    // Optional-details dialog for side-effect-free document fields.
    const handleEditDetails = useCallback((expense: ExpenseDoc) => {
        setExpensesId(expense.id);
        setDetailsOpen(true);
    }, []);

    return (
        <DBlock>
            <CCardList<ExpenseDoc, ExpensesSortableFields>
                title={'Gastos'}
                data={expenses}
                loading={loading}
                keyOf={(expense) => expense.id}
                renderItem={(expense) => (
                    <ExpenseCard
                        expense={expense}
                        onEdit={handleEditExpenses}
                        onEditDetails={handleEditDetails}
                        onDelete={handleDeleteExpenses}
                    />
                )}
                skip={skip}
                take={take}
                count={count}
                setSkip={setSkip}
                filterValue={filter}
                onFilterChange={setFilter}
                filterHelperText={'Folio, factura, notas, tipo de recibo, proveedor'}
                activeFilterCount={
                    (accountId != null ? 1 : 0) +
                    (resourceId != null ? 1 : 0) +
                    (startDate && endDate ? 1 : 0) +
                    (noReceipt ? 1 : 0) +
                    (noSupplement ? 1 : 0) +
                    (isTransferComplete ? 1 : 0)
                }
                filters={
                    <>
                        <DIdAutocomplete
                            id={accountId}
                            onChange={setAccountId}
                            itemText={'compound_name'}
                            label={'Proveedor'}
                            options={data?.getAccounts || []}
                            disableGutters
                        />
                        <DIdAutocomplete
                            id={resourceId}
                            onChange={setResourceId}
                            itemText={'name'}
                            label={'Recurso'}
                            options={data?.getResources || []}
                            disableGutters
                        />
                        <DDateRangePicker
                            label={'Fecha'}
                            value={startDate && endDate ? [dayjs(startDate), dayjs(endDate)] : undefined}
                            onChange={(range: DateRange) => {
                                setStartDate(range[0].format(DATE_FORMAT));
                                setEndDate(range[1].format(DATE_FORMAT));
                            }}
                            onClear={() => { setStartDate(''); setEndDate(''); }}
                            disableGutters
                        />
                        <DCheckbox align={'left'} disableGutters onChange={setNoReceipt} value={noReceipt} label={'Sin Recibo'} />
                        <DCheckbox align={'left'} disableGutters onChange={setNoSupplement} value={noSupplement} label={'Faltantes por recibir complemento'} />
                        <DCheckbox align={'left'} disableGutters onChange={setIsTransferComplete} value={isTransferComplete} label={'Pendientes por transferir'} />
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => setGenerateOpen(true)}
                        >
                            Generar recurrentes
                        </Button>
                    </>
                }
                sortOptions={[
                    { label: 'Fecha', field: ExpensesSortableFields.Date },
                ]}
                sort={sort}
                onSortToggle={toggleSort}
                onItemDoubleClick={handleEditExpenses}
                contextItems={[
                    {
                        title: shortLabel(COLUMN_LABELS.edit),
                        callback: (expense) => handleEditExpenses(expense),
                    },
                    {
                        title: shortLabel(COLUMN_LABELS.delete),
                        disable: (expense) => !expense.is_deletable,
                        callback: (expense) => handleDeleteExpenses(expense),
                    },
                ]}
            />

            <SUpsertExpenseDialog open={upsertOpen} setOpen={setUpsertOpen} expenseId={expenseId} />
            <SDeleteExpenseDialog open={deleteOpen} setOpen={setDeleteOpen} expenseId={expenseId} />
            <SUpdateExpenseDetailsDialog open={detailsOpen} setOpen={setDetailsOpen} expenseId={expenseId} />
            <SGenerateRecurringExpensesDialog open={generateOpen} setOpen={setGenerateOpen} />
        </DBlock>
    );
}
